"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, ArrowRight, ShieldPlus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactInformation() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const contactDetails = [{
    icon: <Phone className="size-5 text-primary" />,
    title: "Phone Number",
    value: "201-736-4633",
    href: "tel:2017364633",
    actionText: "Call Now"
  }, {
    icon: <Phone className="size-5 text-primary" />,
    title: "Secondary Line",
    value: "646-406-4601",
    href: "tel:6464064601",
    actionText: "Call Now"
  }, {
    icon: <Mail className="size-5 text-primary" />,
    title: "Email Address",
    value: "info@showtimeconstructionllc.com",
    href: "mailto:info@showtimeconstructionllc.com",
    actionText: "Send Email"
  }, {
    icon: <MapPin className="size-5 text-primary" />,
    title: "Office Location",
    value: "New Jersey & New York Area",
    href: "#",
    actionText: "View on Map"
  }];
  const clinicImage = {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1521869/img/showtime_construction_site_exterior.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1521869/img/showtime_construction_site_exterior.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1521869/img/showtime_construction_site_exterior.png-wvc-sizes",
    alt: "Showtime Construction professional project site"
  };
  return <section data-section-id="255199" id="contact-information" className="bg-background text-foreground relative overflow-hidden py-16 md:py-24 lg:py-32" ref={sectionRef}>
      {}
      <div className="bg-primary/5 absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 size-[800px] blur-3xl" />
      <div className="bg-secondary/5 absolute right-0 bottom-0 translate-x-1/3 translate-y-1/3 size-[600px] blur-3xl" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
          
          {}
          <div className={`flex flex-col space-y-8 transition-all duration-1000 ${isSectionVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-md bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                <span className="relative flex size-2">
                  <span className="bg-primary absolute inline-flex h-full w-full animate-ping rounded-md opacity-75"></span>
                  <span className="bg-primary relative inline-flex size-2 rounded-md"></span>
                </span>
                Available for New Projects
              </div>
              <h2 className="font-default text-foreground text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
                Get in Touch with Showtime Construction
              </h2>
              <p className="text-muted-foreground max-w-xl text-lg leading-relaxed font-default">
                Ready to start your next renovation or build? Our team is standing by to provide expert guidance and professional estimates for your project.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              {contactDetails.map((detail, i) => <div key={i} data-index={i} className="flex flex-col space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-lg">
                      {detail.icon}
                    </div>
                    <span className="text-muted-foreground text-sm font-medium font-default">{detail.title}</span>
                  </div>
                  {detail.href ? <Link to={detail.href} className="text-foreground hover:text-primary group inline-flex items-center gap-1 text-lg font-semibold transition-colors font-default">
                      {detail.value}
                      {detail.actionText && <ArrowRight className="size-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />}
                    </Link> : <span className="text-foreground text-lg font-semibold font-default">{detail.value}</span>}
                </div>)}
            </div>

            <div className="bg-border h-px w-full" />

            <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
              <span className="text-foreground font-default font-medium">Direct Connect:</span>
              <div className="flex gap-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="icon" className="rounded-md border-primary/20 hover:bg-primary/10 hover:text-primary shadow-md" asChild>
                        <Link to="#">
                          <MessageCircle className="size-5" />
                          <span className="sr-only">WhatsApp</span>
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Message on WhatsApp</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="icon" className="rounded-md border-primary/20 hover:bg-primary/10 hover:text-primary shadow-md" asChild>
                        <Link to="#">
                          <Send className="size-5" />
                          <span className="sr-only">Telegram</span>
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Message on Telegram</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`relative transition-all duration-1000 delay-300 ${isImageVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg shadow-strong lg:aspect-square">
              <img src={clinicImage.src} data-wvc-srcset={clinicImage.srcset} data-wvc-sizes={clinicImage.sizes} alt={clinicImage.alt} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" />
              
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>

            {}
            <Card className="bg-background/90 absolute -bottom-6 -left-6 w-64 border-border shadow-medium backdrop-blur-md sm:-bottom-10 sm:-left-10">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 text-primary flex size-12 shrink-0 items-center justify-center rounded-lg">
                    <ShieldPlus className="size-6" />
                  </div>
                  <div>
                    <h4 className="font-default text-foreground font-semibold">Need an Estimate?</h4>
                    <p className="text-muted-foreground mt-1 text-sm font-default">
                      Free consultations for all new construction projects
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}